import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { LoadingBoxComponent } from '../../../../../shared/components/loading-box/loading-box.component';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ControleEstoqueService } from '../../../../../shared/services/apis/produto/controle-estoque.service';
import { ControleEstoqueListaRequest } from '../../../../../shared/services/requests/produto/controle-estoque-lista.request';
import { ControleEstoquePrintStruct } from '../../../../../shared/services/structs/produto/controle-estoque-print.struct';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';

export interface ImpressaoControleEstoqueData {
  filtros: ControleEstoqueListaRequest;
}

interface ControleEstoquePrintDisplayRow {
  produto: ControleEstoquePrintStruct;
  isVariacao: boolean;
  isLocal: boolean;
  localizacao?: string;
  saldoFisico?: number;
}

@Component({
  selector: 'app-impressao-controle-estoque',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatTableModule, LoadingBoxComponent, AbntPipe],
  templateUrl: './impressao-controle-estoque.component.html',
  styleUrls: ['./impressao-controle-estoque.component.css'],
})
export class ImpressaoControleEstoqueComponent implements OnInit {
  public isLoading: boolean = false;
  public relatorioProdutos: ControleEstoquePrintStruct[] = [];
  public linhas: ControleEstoquePrintDisplayRow[] = [];
  public displayedColumns: string[] = ['codigoSku', 'descricao', 'preco', 'estoque', 'unidade', 'localizacao'];

  constructor(
    public dialogRef: MatDialogRef<ImpressaoControleEstoqueComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImpressaoControleEstoqueData,
    private controleEstoqueService: ControleEstoqueService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.buscarRelatorio();
  }

  private buscarRelatorio(): void {
    this.isLoading = true;
    const request = new ControleEstoqueListaRequest();
    Object.assign(request, this.data.filtros);

    this.controleEstoqueService.getPrint(request).subscribe({
      next: res => {
        this.isLoading = false;
        if (!res.isError) {
          this.relatorioProdutos = res.list || [];
          this.montarLinhas();
          return;
        }

        this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
        this.fechar();
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao gerar relatório de controle de estoque', AlertType.error);
        this.fechar();
      },
    });
  }

  private montarLinhas(): void {
    const linhas: ControleEstoquePrintDisplayRow[] = [];

    const adicionarLocais = (entidade: ControleEstoquePrintStruct, isVariacao: boolean): void => {
      if (entidade.locais?.length > 1) {
        for (const local of entidade.locais) {
          linhas.push({
            produto: entidade,
            isVariacao,
            isLocal: true,
            localizacao: local.localizacao || local.codigoLocalizacao || 'Local não informado',
            saldoFisico: local.saldoFisico,
          });
        }
      }
    };

    for (const produto of this.relatorioProdutos) {
      linhas.push({ produto, isVariacao: false, isLocal: false });

      if (produto.variacoes?.length) {
        // Nível 2: variações; Nível 3: locais de cada variação.
        for (const variacao of produto.variacoes) {
          linhas.push({ produto: variacao, isVariacao: true, isLocal: false });
          adicionarLocais(variacao, true);
        }
      } else {
        adicionarLocais(produto, false);
      }
    }

    this.linhas = linhas;
  }

  public getLocalizacao(row: ControleEstoquePrintDisplayRow): string {
    if (row.isLocal) return row.localizacao || 'Local não informado';
    return row.produto.localizacao || 'Sem local';
  }

  public getEstoque(row: ControleEstoquePrintDisplayRow): number {
    return row.isLocal ? row.saldoFisico || 0 : row.produto.saldoFisico;
  }

  public imprimir(): void {
    const conteudoParaImprimir = document.querySelector('.modal-print-container');

    if (!conteudoParaImprimir) return;

    const janelaImpressao = window.open('', '', 'height=600,width=900');
    if (!janelaImpressao) return;

    const styles = Array.from(document.styleSheets)
      .map(styleSheet => {
        try {
          return Array.from(styleSheet.cssRules)
            .map(rule => rule.cssText)
            .join('');
        } catch {
          return '';
        }
      })
      .join('');

    janelaImpressao.document.write(`
      <html>
        <head>
          <title>Imprimir Controle de Estoque</title>
          <base href="/">
          <style>
            ${styles}
            html, body {
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
              margin: 20px;
              font-family: Roboto, "Helvetica Neue", sans-serif;
              color: #000;
              background: #fff;
            }
            .no-print { display: none !important; }
            .modal-print-container {
              display: block !important;
              width: 100% !important;
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
              box-shadow: none !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            .mat-mdc-table { background: white !important; border-spacing: 0 !important; }
            .mat-mdc-header-cell, .mat-mdc-cell { border: 0 !important; color: black !important; }
            .mat-mdc-header-cell { font-weight: bold !important; border-bottom: 2px solid black !important; }
            .mat-mdc-cell { border-bottom: 1px solid #888 !important; }
          </style>
        </head>
        <body>
          ${conteudoParaImprimir.innerHTML}
        </body>
      </html>
    `);

    janelaImpressao.document.close();
    janelaImpressao.focus();

    setTimeout(() => {
      janelaImpressao.print();
      janelaImpressao.close();
    }, 800);
  }

  public fechar(): void {
    this.dialogRef.close();
  }
}
